/**
 * Сезонные испытания (ТЗ FR-GM-07).
 *
 * Сезон — календарный месяц с одной целью. Не второе название недельного
 * задания: у него другой горизонт, и человек видит по нему не «что успеть
 * сегодня», а «каким был этот месяц».
 *
 * Испытание не выпадает случайно: оно выводится из номера месяца и потому
 * предсказуемо на год вперёд. Случайную цель нельзя ни спланировать, ни
 * объяснить, а необъяснимая цель — это игровой автомат, а не обучение
 * (ТЗ FR-GM-09).
 *
 * Ничего не сгорает и не отнимается: незакрытый сезон не наказывает, а
 * закрытый не запирает следующий. Обратного отсчёта на экране нет — сезон
 * говорит, сколько сделано, а не сколько осталось до расплаты.
 */

import { CHARACTERS_PER_SESSION, DAYS_PER_WEEK_TARGET } from './achievements.js';

/** Что измеряет испытание сезона. */
export const SEASON_CHALLENGES = ['practice-days', 'characters', 'clean-days'] as const;
export type SeasonChallenge = (typeof SEASON_CHALLENGES)[number];

/**
 * Сколько недель в месяце для пересчёта недельных целей.
 *
 * Четыре, а не 4.35: цель месяца — это четыре недельных цели подряд, и
 * округление вверх превратило бы её в «неделя сверху за то, что месяц
 * длинный».
 */
export const WEEKS_PER_SEASON = 4;

/** Сколько чистых дней в неделю просит недельное задание. */
const CLEAN_DAYS_PER_WEEK = 3;

/** Цель испытания: та же недельная планка, взятая четыре раза. */
export const SEASON_TARGETS: Readonly<Record<SeasonChallenge, number>> = Object.freeze({
  'practice-days': DAYS_PER_WEEK_TARGET * WEEKS_PER_SEASON,
  characters: CHARACTERS_PER_SESSION * DAYS_PER_WEEK_TARGET * WEEKS_PER_SEASON,
  'clean-days': CLEAN_DAYS_PER_WEEK * WEEKS_PER_SEASON,
});

/** Состояние сезона: что за месяц, что в нём делают и сколько уже сделано. */
/**
 * Номер месяца.
 *
 * Перечислен, а не «число»: по нему экран берёт название месяца, и число
 * тринадцать означало бы сырой идентификатор вместо слова.
 */
export type SeasonMonth = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

/** Месяцев в году: верхняя граница номера. */
export const MONTHS_IN_YEAR = 12;

export interface SeasonStatus {
  /** Идентификатор сезона: `YYYY-MM`. */
  readonly id: string;
  /** Номер месяца: по нему экран берёт название. */
  readonly month: SeasonMonth;
  readonly year: number;
  readonly challenge: SeasonChallenge;
  readonly target: number;
  readonly current: number;
  readonly done: boolean;
}
